<template>
  <section class="h-full mx-4" id="materialLastVisitValuesManagement">
    <BaseTable
      v-if="materialLastVisitValues"
      :headers="tableHeaders"
      :list="materialLastVisitValues"
      :total-records="materialLastVisitValuesTotalCount"
      table-id="materialLastVisitValues-management"
      @refresh-data="onRefreshData"
      @apply-filters="onApplyFilters"
      @reset-filters="onRefreshData({})"
      :is-add-new-hidden="true">
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import type { TableFilterData } from '@/types/Base';
import type { MaterialLastVisitValue } from '@/types/Materials';
import { getMaterialLastVisitValuesWithConditions } from '@/services/api/priceCollection';
import { filterData, sortData } from '@/utils/table';
import { isoDateToFullDate } from '@/utils/date';
import { useI18n } from 'vue-i18n';

const materialLastVisitValues = ref<MaterialLastVisitValue[]>();
const materialLastVisitValuesTotalCount = ref<number>();
const filterIsActive = ref(false);

const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = [
  { value: 'material.name', text: $t('material.material'), type: 'text', active: true },
  { value: 'traderSite.name', text: 'Trader Site', type: 'text', active: true },
  { value: 'alterCode.name', text: $t('material.alterCode'), type: 'text', active: true },
  { value: 'lastPrice', text: $t('salesDocuments.lastPrice'), type: 'text', active: true },
  { value: 'lastDate', text: $t('salesDocuments.lastDate'), type: 'text', active: true },
  { value: 'shelf', text: $t('salesDocuments.shelf'), type: 'text', active: true },
  { value: 'faces', text: $t('salesDocuments.faces'), type: 'text', active: true },
  { value: 'havePromo', text: $t('salesDocuments.havePromo'), type: 'text', active: true },
  { value: 'notExist', text: $t('salesDocuments.notExist'), type: 'check', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

type QueryParams = {
  page?: number;
  itemsPerPage: number;
  query?: string;
  sortColumn?: string;
  sortAscending?: boolean;
  isActive?: boolean;
};

onMounted(async () => {
  await loadData({
    itemsPerPage: 50,
    sortColumn: 'material.name',
    sortAscending: true,
    page: 1,
  });
});

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value;
  onRefreshData(data);
}

async function onRefreshData(data: TableFilterData) {
  const sortColumn = data.column !== undefined ? tableHeaders[data.column]?.value : 'id';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params: QueryParams = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive: data.isActive,
  };

  await loadData(params);

  if (data.query && materialLastVisitValues.value) {
    materialLastVisitValues.value = filterData(materialLastVisitValues.value, data.query, [
      'material.name',
      'traderSite.name',
      'alterCode.name',
    ]);
  }
  if (data.column !== undefined && data.order && materialLastVisitValues.value) {
    materialLastVisitValues.value = sortData(
      materialLastVisitValues.value,
      data.column,
      data.order,
      tableHeaders
    );
  }
}

async function loadData(params: QueryParams) {
  const res = await getMaterialLastVisitValuesWithConditions(params);
  if (res) {
    materialLastVisitValues.value = res.data.map((item) => ({
      ...item,
      lastDate: isoDateToFullDate(item.lastDate),
    }));
    materialLastVisitValuesTotalCount.value = res.count;
  }
}
</script>
